export const brandThemeRawColors = {
  contrast: {
    light: '#FFFFFF',
    dark: '#191F28',
    black: '#000000',
  },
  preset: {
    heydayIndigo: {
      accent: '#147D78',
      primary: '#171C8F',
      navigation: '#FAFAFE',
      rail: '#171C8F',
    },
    oceanBlue: {
      accent: '#986B24',
      primary: '#1467B8',
      navigation: '#123B5D',
    },
    forestGreen: {
      accent: '#6D55A3',
      primary: '#287A54',
      navigation: '#174A34',
    },
    deepTeal: {
      accent: '#AC6139',
      primary: '#147D78',
      navigation: '#164C4A',
    },
    royalViolet: {
      accent: '#177E89',
      primary: '#6B46C1',
      navigation: '#463075',
    },
    burgundy: {
      accent: '#337B81',
      primary: '#A61E4D',
      navigation: '#641C34',
    },
    terracotta: {
      accent: '#387B70',
      primary: '#B24F32',
      navigation: '#6E3025',
    },
    custom: {
      accent: '#147D78',
      primary: '#4F46E5',
      navigation: '#312E81',
    },
  },
  surface: {
    cool: { pageCanvas: '#F6F6FB', subtle: '#EEF0F7', raised: '#F7F8FA', divider: '#E2E3ED', strongDivider: '#CDCFDE' },
    neutral: { pageCanvas: '#F5F6F7', subtle: '#EFF1F3', raised: '#F8F9FA', divider: '#E2E5E8', strongDivider: '#CCD1D6' },
    warm: { pageCanvas: '#F8F6F2', subtle: '#F2EEE8', raised: '#FAF8F5', divider: '#E8E2D9', strongDivider: '#D7CEC1' },
  },
  default: {
    accentStrong: '#106762',
    accentText: '#106762',
    accentSoft: '#ECF5F4',
    actionDark: '#10156F',
    actionSoft: '#F3F4FF',
    brandBorder: '#C9CDF5',
    brandSky: '#ECEEFF',
    selectedHoverSurface: '#D9DCF8',
    selectedNavigationSurface: '#E3E5FF',
    compactSelectedSurface: '#EAEBFF',
    navigationText: '#56616F',
    navigationMutedText: '#626D7B',
    navigationDisabledText: '#B0B8C1',
    navigationDivider: '#E2E3ED',
    navigationBadgeSurface: '#EEF0F7',
    navigationFocus: '#171C8F',
    railDivider: 'rgba(255, 255, 255, 0.08)',
    railMuted: 'rgba(255, 255, 255, 0.64)',
    railFocus: '#FFFFFF',
    railSelected: 'rgba(255, 255, 255, 0.16)',
    brandShadow: 'rgba(23, 28, 143, 0.18)',
    focus: 'rgba(23, 28, 143, 0.24)',
    focusStrong: '#171C8F',
  },
} as const;

// Canvas의 4px 기준 간격을 역할별 layout token에서 공유합니다.
const space = { none: 0, xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32 } as const;
// Canvas Kit v16의 Sana shape scale. 실제 반경은 각 컴포넌트 역할이 선택합니다.
const shape = { none: '0px', xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px', xxl: '20px', xxxl: '28px', full: '999px' } as const;
// Sana의 geometry·alpha에 APEX 중립색을 연결합니다. 역할별 적용은 shadow가 소유합니다.
const depth = {
  none: 'none',
  1: '0 1px 4px -1px rgba(25, 31, 40, 0.03), 0 2px 8px rgba(25, 31, 40, 0.02)',
  2: '0 2px 8px -1px rgba(25, 31, 40, 0.05), 0 4px 16px rgba(25, 31, 40, 0.0334)',
  3: '0 3px 12px -1px rgba(25, 31, 40, 0.07), 0 6px 24px rgba(25, 31, 40, 0.0467)',
  4: '0 4px 16px -2px rgba(25, 31, 40, 0.09), 0 8px 32px rgba(25, 31, 40, 0.06)',
  5: '0 8px 24px -2px rgba(25, 31, 40, 0.11), 0 16px 48px rgba(25, 31, 40, 0.0734)',
  6: '0 16px 48px -3px rgba(25, 31, 40, 0.13), 0 32px 96px rgba(25, 31, 40, 0.0867)',
} as const;

// Canvas Kit v16의 S/M/L 규격. 터치 환경의 최소 44px는 앱 공통 정책입니다.
const button = {
  fontWeight: 500,
  disabledOpacity: 0.4,
  feedbackDuration: 120,
  pressedDuration: 40,
  sizes: {
    small: { height: 32, minWidth: 72, paddingInline: 12, iconInset: 8, iconSize: 20, iconGap: 4, fontSize: '0.875rem', lineHeight: '1.25rem' },
    medium: { height: 40, minWidth: 88, paddingInline: 16, iconInset: 12, iconSize: 20, iconGap: 6, fontSize: '0.875rem', lineHeight: '1.25rem' },
    large: { height: 48, minWidth: 104, paddingInline: 20, iconInset: 16, iconSize: 24, iconGap: 8, fontSize: '1rem', lineHeight: '1.5rem' },
  },
} as const;

export const designTokens = {
  space,
  shape,
  depth,
  button,
  feedback: {
    notice: { inset: space.md, compactInset: space.xs, gap: space.sm, iconSize: 20, radius: shape.sm },
    toast: { width: 360, blockInset: space.sm, inlineInset: space.md, radius: shape.xl, shadow: depth[5] },
    result: { minHeight: 148, iconSize: 44, compactIconSize: 32, glyphSize: 24, compactGlyphSize: 20 },
  },
  loading: {
    dots: { size: 16, gap: space.xs, duration: 1400, delay: 160 },
    skeleton: { lineHeight: 16, headingHeight: 24, shapeRadius: shape.sm, duration: 800 },
  },
  progress: { markerSize: 24, iconSize: 16, labelWidth: 110, barHeight: 6 },
  motion: {
    feedback: { duration: 100, easing: 'cubic-bezier(0.2, 0, 0.2, 1)' },
    menu: { duration: 80, easing: 'linear' },
    input: { duration: 200, easing: 'ease' },
    tab: { duration: 150, easing: 'ease' },
    expand: { duration: 200, easing: 'cubic-bezier(0.35, 0, 0.05, 1)' },
    enter: { duration: 250, easing: 'cubic-bezier(0.05, 0.4, 0.3, 1)' },
    exit: { duration: 200, easing: 'cubic-bezier(0.4, 0, 0.95, 0.8)' },
  },
  typography: {
    fontFamily: '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans KR", sans-serif',
    pageTitle: { fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 700 },
    pageHeaderAction: { fontSize: '0.875rem', lineHeight: '1.25rem' },
    sectionCardAction: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: button.fontWeight },
    sectionTitle: { fontSize: '1.25rem', lineHeight: '1.75rem', fontWeight: 700 },
    panelTitle: { fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 700 },
    componentTitle: { fontSize: '1rem', lineHeight: '1.5rem', fontWeight: 700 },
    subsectionTitle: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 600 },
    body: { fontSize: '1rem', lineHeight: '1.5rem' },
    ui: { fontSize: '0.875rem', lineHeight: '1.25rem' },
    supporting: { fontSize: '0.75rem', lineHeight: '1rem' },
    metricValue: { fontSize: '1.5rem', lineHeight: '2rem' },
  },
  radius: {
    /** MUI sx의 숫자형 borderRadius에 적용되는 기본 배율입니다. */
    muiBase: 2,
    compact: '2px',
    badge: shape.xs,
    button: shape.full,
    input: shape.lg,
    search: shape.lg,
    portrait: shape.md,
    control: shape.xs,
    navigation: shape.lg,
    tab: shape.lg,
    menuItem: shape.lg,
    table: shape.lg,
    surface: shape.xxl,
    popover: shape.xxl,
    // 짧은 tooltip은 pill로 보이고, 여러 줄은 모서리가 본문을 침범하지 않게 상한을 둡니다.
    tooltip: shape.xxl,
    dialog: shape.xxxl,
    /** 기존 소비자 호환용입니다. 새 팝오버와 대화상자는 역할별 token을 사용합니다. */
    overlay: shape.lg,
    pill: shape.full,
    circle: '50%',
  },
  color: {
    canvas: '#FFFFFF',
    navigationSurface: '#F7F7FC',
    sidebarSurface: '#FAFAFE',
    railSurface: 'var(--app-brand-rail-surface, #171C8F)',
    overviewSurface: 'var(--app-page-canvas, #F6F6FB)',
    pageCanvas: 'var(--app-page-canvas, #F6F6FB)',
    subtleSurface: 'var(--app-subtle-surface, #EEF0F7)',
    hoverSurface: 'var(--app-brand-hover-surface, #F3F4FF)',
    selectedHoverSurface: 'var(--app-brand-selected-hover-surface, #D9DCF8)',
    selectedNavigationSurface: 'var(--app-brand-selected-navigation-surface, #E3E5FF)',
    compactSelectedSurface: 'var(--app-brand-compact-selected-surface, #EAEBFF)',
    neutralRaisedSurface: 'var(--app-neutral-raised-surface, #F7F8FA)',
    tableHeaderSurface: '#FFFFFF',
    primaryText: '#191F28',
    secondaryText: '#56616F',
    mutedText: '#626D7B',
    disabledText: '#B0B8C1',
    faintText: '#C1C7CD',
    neutralText: '#56616F',
    divider: 'var(--app-divider, #E2E3ED)',
    tableCellDivider: 'rgba(25, 31, 40, 0.0745)',
    strongDivider: 'var(--app-strong-divider, #CDCFDE)',
    action: 'var(--app-brand-action, #171C8F)',
    accent: 'var(--app-brand-accent, #147D78)',
    accentStrong: 'var(--app-brand-accent-strong, #106762)',
    accentSoft: 'var(--app-brand-accent-soft, #ECF5F4)',
    accentText: 'var(--app-brand-accent-text, #106762)',
    accentContrast: 'var(--app-brand-accent-contrast, #FFFFFF)',
    pageHeading: 'var(--app-page-heading, #191F28)',
    pageSupportingText: 'var(--app-page-supporting-text, #56616F)',
    pageErrorText: 'var(--app-page-error-text, #CA1D2D)',
    actionDark: 'var(--app-brand-action-dark, #10156F)',
    actionSoft: 'var(--app-brand-action-soft, #F3F4FF)',
    brandNavy: '#171C8F',
    brandBorder: 'var(--app-brand-border, #C9CDF5)',
    brandSky: 'var(--app-brand-sky, #ECEEFF)',
    info: '#2563EB',
    infoDark: '#1B64DA',
    infoSoft: '#E8F3FF',
    success: '#00A661',
    successDark: '#0E8A4A',
    successText: '#087A40',
    successSoft: '#E8F8F0',
    warning: '#F59F00',
    warningDark: '#A95300',
    warningSoft: '#FFF7E6',
    warningHoverSoft: '#FFEDC2',
    warningBorder: '#FFE3A1',
    warningAccent: '#FFB934',
    warningAccentText: '#5C3B00',
    danger: '#E42939',
    dangerText: '#CA1D2D',
    dangerSoft: '#FFF0F1',
    inverseSurface: '#102A43',
    inverseRaisedSurface: '#223F5B',
    inverseDivider: '#3A556F',
    inverseText: '#FFFFFF',
    inverseSecondaryText: '#C7D4E2',
    inverseMutedText: '#9FB2C7',
    inverseFaintText: '#AFC2D4',
    inverseSuccess: '#8ED1C6',
    inverseSuccessSurface: '#1F7A6E',
    inverseSuccessText: '#DDF8F2',
    inverseSuccessBorder: '#4FA99D',
    inverseBorder: '#48637D',
  },
  navigation: {
    surface: 'var(--app-brand-navigation-surface, #FAFAFE)',
    divider: 'var(--app-brand-navigation-divider, #E2E3ED)',
    text: 'var(--app-brand-navigation-text, #56616F)',
    mutedText: 'var(--app-brand-navigation-muted-text, #626D7B)',
    disabledText: 'var(--app-brand-navigation-disabled-text, #B0B8C1)',
    hoverSurface: 'var(--app-brand-navigation-hover-surface, #F3F4FF)',
    hoverText: 'var(--app-brand-navigation-hover-text, #191F28)',
    selectedSurface: 'var(--app-brand-navigation-selected-surface, #EAEBFF)',
    selectedHoverSurface: 'var(--app-brand-navigation-selected-hover-surface, #E3E5FF)',
    selectedText: 'var(--app-brand-navigation-selected-text, #10156F)',
    selectedHoverText: 'var(--app-brand-navigation-selected-hover-text, #10156F)',
    selectedIcon: 'var(--app-brand-navigation-selected-icon, #171C8F)',
    selectedHoverIcon: 'var(--app-brand-navigation-selected-hover-icon, #171C8F)',
    selectedBadgeSurface: 'var(--app-brand-navigation-selected-badge-surface, #171C8F)',
    selectedBadgeText: 'var(--app-brand-navigation-selected-badge-text, #FFFFFF)',
    badgeSurface: 'var(--app-brand-navigation-badge-surface, #EEF0F7)',
    badgeText: 'var(--app-brand-navigation-badge-text, #56616F)',
    primaryModeSurface: 'var(--app-brand-navigation-primary-mode-surface, #F3F4FF)',
    primaryModeText: 'var(--app-brand-navigation-primary-mode-text, #10156F)',
    primaryModeHoverSurface: 'var(--app-brand-navigation-primary-mode-hover-surface, #E3E5FF)',
    primaryModeHoverText: 'var(--app-brand-navigation-primary-mode-hover-text, #10156F)',
    secondaryModeSurface: 'var(--app-brand-navigation-secondary-mode-surface, #FFFFFF)',
    secondaryModeText: 'var(--app-brand-navigation-secondary-mode-text, #56616F)',
    secondaryModeHoverSurface: 'var(--app-brand-navigation-secondary-mode-hover-surface, #F3F4FF)',
    secondaryModeHoverText: 'var(--app-brand-navigation-secondary-mode-hover-text, #56616F)',
    focus: 'var(--app-brand-navigation-focus, #171C8F)',
  },
  rail: {
    surface: 'var(--app-brand-rail-surface, #171C8F)',
    text: 'var(--app-brand-rail-text, #FFFFFF)',
    mutedText: 'var(--app-brand-rail-muted, rgba(255, 255, 255, 0.64))',
    divider: 'var(--app-brand-rail-divider, rgba(255, 255, 255, 0.08))',
    focus: 'var(--app-brand-rail-focus, #FFFFFF)',
    selectedSurface: 'var(--app-brand-rail-selected, rgba(255, 255, 255, 0.16))',
  },
  overlay: {
    modalBackdrop: 'rgba(25, 31, 40, 0.3529)',
    navigationBackdrop: 'rgba(25, 31, 40, 0.56)',
    detailBackdrop: 'rgba(25, 31, 40, 0.12)',
    brandShadow: 'var(--app-brand-shadow, rgba(23, 28, 143, 0.18))',
    focus: 'var(--app-brand-focus, rgba(23, 28, 143, 0.24))',
    focusStrong: 'var(--app-brand-focus-strong, #171C8F)',
    surfaceShadow: 'rgba(23, 28, 143, 0.055)',
    scrimSurface: 'rgba(15, 23, 42, 0.72)',
    scrimHoverSurface: 'rgba(15, 23, 42, 0.86)',
    scrimBorder: 'rgba(255, 255, 255, 0.4)',
    railDivider: 'var(--app-brand-rail-divider, rgba(255, 255, 255, 0.08))',
    railMuted: 'var(--app-brand-rail-muted, rgba(255, 255, 255, 0.64))',
    railFocus: 'var(--app-brand-rail-focus, #FFFFFF)',
    railSelected: 'var(--app-brand-rail-selected, rgba(255, 255, 255, 0.16))',
  },
  shadow: {
    inputFocusInset: 'inset 0 0 0 1px var(--app-brand-action, #171C8F)',
    inputFocus: '0 0 0 3px color-mix(in srgb, var(--app-brand-action, #171C8F) 14%, transparent)',
    inputErrorFocus: '0 0 0 3px rgba(228, 41, 57, 0.14)',
    floatingControl: depth[2],
    menu: depth[3],
    tooltip: depth[3],
    anchoredDialog: depth[4],
    overlay: depth[6],
    raisedSurface: depth[5],
    selectedControl: '0 3px 10px rgba(15, 23, 42, 0.2)',
    surface: depth[1],
    surfaceHover: depth[2],
  },
  media: {
    touchTarget: '@media (max-width: 599.95px), (pointer: coarse)',
  },
  layout: {
    detailPanelWidth: 420,
    detailPanelCompactWidth: 360,
    listDetailMinViewport: 1440,
    detailInlineInset: space.md,
    detailHeaderBlockInset: space.sm,
    detailHeaderMinHeight: 64,
    controlMinHeight: 40,
    inputInset: space.xs,
    inputAdornmentGap: space.xs,
    dropdownInlineInset: space.sm,
    dropdownIndicatorGap: space.xxs,
    dropdownListInset: space.xs,
    dropdownOptionInlineInset: space.sm,
    buttonInlineInset: button.sizes.medium.paddingInline,
    menuItemInlineInset: space.sm,
    menuItemEndInset: space.xs,
    menuItemBlockInset: space.xs,
    menuItemGap: space.xs,
    menuItemSpacing: 2,
    menuItemIconSize: 20,
    menuAnchorGap: space.xs,
    popupAnchorGap: space.xxs,
    popupViewportInset: space.md,
    popupContentPadding: space.xl,
    popupContentPaddingCompact: space.lg,
    tooltipInlineInset: space.sm,
    tooltipBlockInset: 6,
    tooltipAnchorGap: space.xxs,
    tooltipArrowAnchorGap: 14,
    searchIconSize: 16,
    globalSearchMaxWidth: 560,
    globalSearchPanelMaxHeight: 560,
    tableCellInlineInset: space.md,
    touchTargetMinSize: 44,
    listRowMinHeight: 64,
    listRowCompactMinHeight: 44,
    listRowInlineInset: space.md,
    listRowBlockInset: space.sm,
    listRowCompactBlockInset: space.xs,
    listRowGap: space.sm,
    listRowContentGap: space.xxs,
    settingRowIconSize: 40,
    informationRowLabelWidth: 112,
    informationRowGap: space.sm,
    informationRowMinHeight: 32,
    informationGridColumnGap: space.xl,
    informationGridRowGap: space.lg,
    valueBadgeHeight: 24,
    valueBadgeCompactHeight: 20,
    valueBadgeIconSize: 16,
    valueBadgeRadius: shape.md,
    valueBadgeCompactRadius: shape.sm,
    valueBadgeFontWeight: 500,
    valueBadgeGap: space.xxs,
    chipRadius: shape.sm,
    countBadgeSize: 20,
    compactEditButtonHeight: button.sizes.small.height,
    compactEditButtonGlyphSize: button.sizes.small.iconSize,
    sectionCardActionHeight: 40,
    sectionCardActionHeightCompact: 44,
    sectionCardActionMinWidth: button.sizes.medium.minWidth,
    sectionCardActionGlyphSize: button.sizes.medium.iconSize,
    sectionCardBodyPadding: space.md,
    surfacePaddingCompact: space.md,
    surfacePadding: space.xl,
    surfaceActionBlockPadding: space.md,
    dialogContentPaddingCompact: space.xl,
    dialogContentPadding: space.xxl,
    dialogActionBlockPadding: space.xl,
    dialogSectionGap: space.xl,
    dialogSectionGapCompact: space.xs,
    pageSectionGap: space.md,
    pageSectionGroupGap: space.xl,
    formFieldGap: space.sm,
    formSectionGap: space.sm,
    pageTabPanelGap: space.md,
    pillTabHeight: 40,
    productRailWidth: 56,
    sectionSidebarWidth: 220,
    sectionSidebarItemHeight: 40,
    sectionSidebarItemGap: space.xxs,
    sectionSidebarIconSize: 20,
    workspaceHeaderHeight: 56,
    pageHeaderMinHeight: 64,
    pageHeaderActionControlSize: 40,
    pageHeaderActionGlyphSize: 16,
    pageHeaderControlSize: 40,
    pageHeaderControlSizeCompact: 44,
    filterSearchWidth: 260,
    pageHeaderEditActionControlSizeCompact: 44,
    pageHeaderTitleBasis: 240,
    pageHeaderControlGlyphSize: 20,
    pageHeaderControlGlyphSizeCompact: 22,
    pageHeaderFirstLineHeight: 40,
    pageHeaderFirstLineHeightCompact: 44,
    pageHeaderInsetTopCompact: space.sm,
    pageHeaderInsetTop: space.sm,
    pageHeaderInsetBottom: space.sm,
    pageBodyStartGapCompact: space.md,
    pageBodyStartGap: space.md,
    overviewBodyStartGapCompact: 20,
    overviewBodyStartGap: 28,
    pageContentCompactWidth: 960,
    pageContentStandardWidth: 1120,
    pageContentMaxWidth: 1280,
    pageGutterCompact: space.md,
    pageGutter: space.md,
    resourceHeaderHeight: 56,
    resourceRowHeight: 56,
    resourceRowHeightCompact: 56,
    resourceRowHeightMultiline: 64,
    listSkeletonRowHeight: 48,
    workspaceDialogMaxWidth: 1360,
    workspaceDialogStandardMaxWidth: 1120,
    workspaceDialogInlineInset: 24,
    workspaceDialogBlockInset: 40,
    dialogCompactMaxWidth: 520,
    dialogStandardMaxWidth: 760,
    dialogLargeMaxWidth: 960,
    dialogWideMaxWidth: 1536,
    dialogInlineInset: 24,
    dialogBlockInset: 32,
    flowStepsMaxWidth: 520,
    anchoredPopoverCompactWidth: 360,
    anchoredPopoverStandardWidth: 440,
    anchoredPopoverCompactInlineInset: 16,
    anchoredPopoverStandardInlineInset: 16,
    anchoredPopoverBlockInset: 16,
    anchoredPopoverMaxHeight: 620,
  },
} as const;
